import type {
  EmailBlockData,
  EmailBlockType,
} from '@/api/email-builder/email-builder.types.ts';

export type BlockGroup = 'Inhalt' | 'Layout' | 'Produkte';

export interface BlockTypeMeta {
  type: EmailBlockType;
  label: string;
  description: string;
  group: BlockGroup;
  /** Fresh, valid default data for a newly inserted block of this type. */
  createDefault: () => EmailBlockData;
}

/** Registry of every block type the builder can insert. */
export const BLOCK_TYPE_META: Record<EmailBlockType, BlockTypeMeta> = {
  heading: {
    type: 'heading',
    label: 'Überschrift',
    description: 'Abschnittstitel (H1–H3)',
    group: 'Inhalt',
    createDefault: () => ({ text: 'Neue Überschrift', level: 'h2' }),
  },
  text: {
    type: 'text',
    label: 'Text',
    description: 'Formatierter Fließtext',
    group: 'Inhalt',
    createDefault: () => ({ html: '<p></p>' }),
  },
  button: {
    type: 'button',
    label: 'Button',
    description: 'Call-to-Action-Schaltfläche',
    group: 'Inhalt',
    createDefault: () => ({
      label: 'Mehr erfahren',
      href: 'https://',
      variant: 'primary',
    }),
  },
  image: {
    type: 'image',
    label: 'Bild',
    description: 'Bild mit optionalem Link und Bildunterschrift',
    group: 'Inhalt',
    createDefault: () => ({ src: '', alt: '', href: '', caption: '' }),
  },
  'info-box': {
    type: 'info-box',
    label: 'Info-Box',
    description: 'Hinweiskasten (Info / Warnung / Erfolg)',
    group: 'Inhalt',
    createDefault: () => ({ title: '', html: '<p></p>', variant: 'info' }),
  },
  banner: {
    type: 'banner',
    label: 'Banner',
    description: 'Vollbreiter Banner mit optionalem Button',
    group: 'Inhalt',
    createDefault: () => ({
      text: 'Ihr Banner-Text',
      buttonLabel: '',
      buttonHref: '',
    }),
  },
  divider: {
    type: 'divider',
    label: 'Trennlinie',
    description: 'Horizontale Trennlinie',
    group: 'Layout',
    createDefault: () => ({}),
  },
  spacer: {
    type: 'spacer',
    label: 'Abstand',
    description: 'Vertikaler Leerraum',
    group: 'Layout',
    createDefault: () => ({ height: 24 }),
  },
  'product-card': {
    type: 'product-card',
    label: 'Produktkarte',
    description: 'Produkt mit Bild, Artikelnummer und Preis',
    group: 'Produkte',
    createDefault: () => ({
      name: '',
      articleNumber: '',
      imageUrl: '',
      price: '',
      manufacturer: '',
      href: '',
    }),
  },
  'product-spotlight': {
    type: 'product-spotlight',
    label: 'Produkt-Spotlight',
    description: 'Hervorgehobenes Produkt mit großem Bild',
    group: 'Produkte',
    createDefault: () => ({
      name: '',
      articleNumber: '',
      description: '',
      imageUrl: '',
      price: '',
      manufacturer: '',
      buttonLabel: 'Jetzt bestellen',
      buttonHref: '',
    }),
  },
  'product-list': {
    type: 'product-list',
    label: 'Produktliste',
    description: 'Kompakte Tabelle mehrerer Produkte',
    group: 'Produkte',
    createDefault: () => ({ title: '', items: [] }),
  },
};

/** Block types grouped for the "Block hinzufügen" menu. */
export const BLOCK_GROUPS: { group: BlockGroup; types: EmailBlockType[] }[] = [
  {
    group: 'Inhalt',
    types: ['heading', 'text', 'button', 'image', 'info-box', 'banner'],
  },
  { group: 'Layout', types: ['divider', 'spacer'] },
  {
    group: 'Produkte',
    types: ['product-card', 'product-spotlight', 'product-list'],
  },
];

/** Base URL for catalog item links picked via the SAP item picker. */
export const ITEM_LINK_BASE = 'https://service.elink-distribution.com/items';

/** Builds the public catalog URL for a SAP item id (e.g. `MHRV4ZD/A`). */
export function itemLinkFor(itemId: string): string {
  return `${ITEM_LINK_BASE}/${itemId.replace(/\//g, '-')}`;
}

/** Formats a number as a German EUR price string (e.g. `553,00 €`). */
export function formatEur(value: number): string {
  return value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
}
